// components/secure/ZoningBrief.js — the answer the client is actually waiting for.
//
// A licensee records what the client needs and which towns they will consider.
// This reads the SHARED zoning library and says, immediately: here is where that
// use is permitted by right, here is where it needs a hearing, here is where it is
// refused, and here are the towns nobody has researched yet.
//
// The research is shared because zoning_jurisdictions is keyed on municipality +
// use, so a town answered for one client is answered for every client after. Most
// of the time the answer already exists and the client was waiting on nobody —
// which is the whole reason this is automatic rather than a button somebody
// remembers to press.
//
// The unresearched towns are shown as prominently as the answers. A gap the
// licensee can see is work; a gap they cannot see is a client being told "we're
// looking into it" for a fortnight.
import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';

const TONE = {
    by_right: { row: 'bg-green-50 border-green-200', pill: 'bg-green-100 text-green-800', label: 'By right' },
    special_use: { row: 'bg-amber-50 border-amber-200', pill: 'bg-amber-100 text-amber-800', label: 'Hearing needed' },
    not_allowed: { row: 'bg-red-50 border-red-200', pill: 'bg-red-100 text-red-700', label: 'Not permitted' },
    unverified: { row: 'bg-slate-50 border-slate-200', pill: 'bg-slate-200 text-slate-600', label: 'Not researched' },
};

const ZoningBrief = ({ clientId, onSearchStarted }) => {
    const api = useApiClient();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            setData(await api.get(`/api/site-searches/brief/${clientId}`));
            setError('');
        } catch (e) {
            setError(e.message || 'Could not load the zoning brief.');
        } finally { setLoading(false); }
    }, [api, clientId]);
    useEffect(() => { load(); }, [load]);

    const runAuto = async () => {
        setBusy(true); setError(''); setNotice('');
        try {
            const res = await api.post(`/api/site-searches/auto/${clientId}`, {});
            setNotice(res.started
                ? 'Site search opened, and the sweep will run daily.'
                : (res.updated ? 'The open search was refreshed from the client record.' : res.reason));
            load();
            if (res.started && onSearchStarted) onSearchStarted();
        } catch (e) { setError(e.message); } finally { setBusy(false); }
    };

    if (loading && !data) {
        return <div className="text-sm text-slate-500">Reading the zoning library…</div>;
    }
    if (error) return <p className="text-sm text-red-600">{error}</p>;

    // Nothing to answer yet — say what is missing rather than showing an empty box.
    if (!data?.brief) {
        return (
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
                <p className="text-sm font-medium text-slate-700">Zoning brief</p>
                <p className="text-xs text-slate-500 mt-1">
                    {data?.reason || 'Record the client’s use and the towns they will consider, and the '
                     + 'zoning answers will appear here automatically.'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                    The use has to be something a zoning administrator would recognise — “beauty school”,
                    not “commercial”.
                </p>
            </div>
        );
    }

    const s = data.brief.summary;
    const rows = [...data.brief.answered,
                  ...data.brief.needs_research.map((t) => ({ ...t, verdict_status: 'unverified' }))];

    return (
        <div className="border border-slate-200 rounded-md">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-semibold text-slate-800">
                        Zoning brief — {data.use_category}
                    </p>
                    <p className="text-xs text-slate-500">
                        {s.open_now} of {s.towns} town{s.towns === 1 ? '' : 's'} worth a call today
                        {s.by_right > 0 && ` · ${s.by_right} by right`}
                        {s.special_use > 0 && ` · ${s.special_use} by hearing`}
                        {s.not_allowed > 0 && ` · ${s.not_allowed} refused`}
                        {s.unresearched > 0 && ` · ${s.unresearched} to research`}
                    </p>
                </div>
                {data.would_start && (
                    <button onClick={runAuto} disabled={busy}
                        className="text-xs px-2.5 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300">
                        {busy ? 'Starting…' : 'Start the search'}
                    </button>
                )}
                {!data.would_start && data.search_id && (
                    <button onClick={runAuto} disabled={busy}
                        className="text-xs px-2.5 py-1.5 border border-slate-300 text-slate-600 rounded-md hover:bg-slate-100 disabled:opacity-50">
                        {busy ? 'Refreshing…' : 'Refresh from record'}
                    </button>
                )}
            </div>

            {notice && <p className="px-3 pt-2 text-xs text-green-700">{notice}</p>}

            <div className="p-2 space-y-1.5">
                {rows.map((t) => {
                    const tone = TONE[t.verdict_status] || TONE.unverified;
                    return (
                        <div key={t.municipality} className={`border rounded p-2 ${tone.row}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-slate-800">{t.municipality}</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${tone.pill}`}>{tone.label}</span>
                                {t.districts_by_right && (
                                    <span className="text-xs text-slate-600">in {t.districts_by_right}</span>
                                )}
                            </div>
                            {t.verdict && <p className="text-xs text-slate-600 mt-0.5">{t.verdict}</p>}
                            {t.conditions && (
                                <p className="text-xs text-slate-500 mt-0.5">Conditions: {t.conditions}</p>
                            )}
                            {t.verdict_status === 'unverified' && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Nobody has answered this town for this use yet. Researching it once adds it
                                    to the shared library for every future client.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ZoningBrief;
